import { Box, Button, Icon, Text } from "@showitinc/showit-wc";
import React from "react";
import { previewPublishClicked } from "@/adaptors/AnalyticsAdaptors";
import SitePublishActions from "../../actions/SitePublishActions";
import UIActions from "../../actions/UIActions";
import UserActions from "../../actions/UserActions";
import Glyphicon from "../../components/base/Glyphicon";
import DesignSaveStatus from "../../components/DesignSaveStatus";
import SubscribeButton from "../../components/SubscribeButton";
import AppConfig from "../../config/app";
import { PREVIEW, PUBLISH_OPEN } from "../../constants/UserEventConstants";
import { useAccountState } from "../../hooks/query/useAccount";
import { getDaysBetween } from "../../utils/DateUtils";
import AccountButton from "../base/AccountButton";
import CountdownTimer from "./CountdownTimer";
import styles from "./Toolbar.module.css";

/**
 * @typedef {import("@/stores/PreviewStore").LambdaConfig} LambdaConfig
 */

const PreviewPublish = () => {
  const user = useAccountState();

  /**
   *
   * @param {LambdaConfig=} config
   */
  const _openPreviewModal = (config) => {
    UIActions.previewOpen(config);
    UserActions.trackEvent({ event: PREVIEW }, { fb: true });
  };

  const _openPublishModal = () => {
    SitePublishActions.openDialog();
    UserActions.trackEvent({ event: PUBLISH_OPEN });
  };

  const _renderTrialReminder = () => {
    const statusExpiration = user.get("statusExpiration");
    let message = "Trial Mode";

    if (statusExpiration) {
      const numDays = getDaysBetween({ end: statusExpiration });
      let suffix = "";

      switch (numDays) {
        case 0:
          suffix = "today";
          break;
        case 1:
          suffix = `in ${numDays} day`;
          break;
        default:
          suffix = `in ${numDays} days`;
          break;
      }

      message = `Your trial ends ${suffix}`;

      return (
        <Box d="flex" align="center">
          <Text variant="heading-2">{message}</Text>
          <SubscribeButton
            style={{
              letterSpacing: "0.1em",
              fontSize: "0.75rem",
              padding: "3px 10px",
              marginLeft: 10,
            }}
          />
        </Box>
      );
    }
  };

  const isActive = user.get("isActive");

  return (
    <>
      {!isActive ? _renderTrialReminder() : null}
      <Box px="2">
        <Button
          onClick={() => _openPreviewModal()}
          variant="minimal"
          size="lg"
          data-testid="preview-site-button"
        >
          <Glyphicon
            icon="preview"
            style={{ fontSize: "1.1em", lineHeight: "1.2em" }}
          />
          &nbsp;Preview
        </Button>
      </Box>

      {isActive && (
        <Button
          onClick={_openPublishModal}
          variant="minimal"
          size="lg"
          icon-prefix="publish"
          data-testid="publish-site-button"
          className={styles.publishButton}
        >
          Publish
        </Button>
      )}
    </>
  );
};

const ToolbarRight = () => {
  const _openSignupModal = () => {
    UIActions.openSignupModal();
    previewPublishClicked();
  };

  const { demoMode } = AppConfig;
  const user = useAccountState();
  const targetLaunchDate = user.get("targetLaunchDate")
    ? new Date(user.get("targetLaunchDate"))
    : null;
  const isExpired = targetLaunchDate < new Date();

  return (
    <Box d="flex" align="center" className={styles.rightToolbarItem}>
      {targetLaunchDate && !isExpired && (
        <CountdownTimer
          targetDate={targetLaunchDate}
          hasLaunched={user.get("hasLaunched")}
        />
      )}
      <DesignSaveStatus />
      {!demoMode ? (
        <PreviewPublish />
      ) : (
        <AccountButton
          trackEvent={() =>
            UserActions.trackPostHogEvent(
              { event: "create_your_site_clicked" },
              { location: "toolbar", demoMode: AppConfig.demoMode },
            )
          }
          onClick={_openSignupModal}
        >
          Create your site
        </AccountButton>
      )}
    </Box>
  );
};

export default ToolbarRight;
